import { ProductInterface } from "../interfaces/ProductInterface";
import { CategoryInterface } from "../interfaces/CategoryInterface";
import { FilterListInterface } from "../interfaces/FilterListInterface";
import { ProductDetailsDataInterface } from "../interfaces/ProductDetailsDataInterface";
import { BrandingInterface } from "../interfaces/BrandingInterface";

const DB_NAME = "BravilorProducts";
const DB_VERSION = 2;

export interface StoredImage {
    url: string;
    blob: Blob;
    timestamp: number;
}

export interface StoredBrandingFile {
    url: string;
    blob: Blob;
    timestamp: number;
}

class IndexedDBManager {
    private db: IDBDatabase | null = null;

    async init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                reject(new Error("Failed to open IndexedDB"));
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Products store
                if (!db.objectStoreNames.contains("products")) {
                    const productsStore = db.createObjectStore("products", {
                        keyPath: "id",
                    });
                    productsStore.createIndex("identifier", "identifier", {
                        unique: false,
                    });
                }

                // Categories store
                if (!db.objectStoreNames.contains("categories")) {
                    db.createObjectStore("categories", { keyPath: "id" });
                }

                // Filters store
                if (!db.objectStoreNames.contains("filters")) {
                    db.createObjectStore("filters", { keyPath: "id" });
                }

                // Branding store
                if (!db.objectStoreNames.contains("branding")) {
                    db.createObjectStore("branding", { keyPath: "id" });
                }

                // Images store
                if (!db.objectStoreNames.contains("images")) {
                    const imagesStore = db.createObjectStore("images", {
                        keyPath: "url",
                    });
                    imagesStore.createIndex("timestamp", "timestamp", {
                        unique: false,
                    });
                }

                // Branding files store
                if (!db.objectStoreNames.contains("brandingFiles")) {
                    const brandingFilesStore = db.createObjectStore(
                        "brandingFiles",
                        {
                            keyPath: "url",
                        },
                    );
                    brandingFilesStore.createIndex("timestamp", "timestamp", {
                        unique: false,
                    });
                }

                // Metadata store for sync information
                if (!db.objectStoreNames.contains("metadata")) {
                    db.createObjectStore("metadata", { keyPath: "key" });
                }
            };
        });
    }

    async storeProducts(products: ProductInterface[]): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["products"], "readwrite");
        const store = transaction.objectStore("products");

        for (const product of products) {
            await new Promise<void>((resolve, reject) => {
                const request = store.put(product);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
    }

    async storeCategories(categories: CategoryInterface[]): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["categories"], "readwrite");
        const store = transaction.objectStore("categories");

        for (const category of categories) {
            await new Promise<void>((resolve, reject) => {
                const request = store.put(category);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
    }

    async storeFilters(filters: FilterListInterface[]): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["filters"], "readwrite");
        const store = transaction.objectStore("filters");

        for (const filter of filters) {
            await new Promise<void>((resolve, reject) => {
                const request = store.put(filter);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
    }

    async storeBranding(branding: BrandingInterface[]): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["branding"], "readwrite");
        const store = transaction.objectStore("branding");

        for (const brandItem of branding) {
            await new Promise<void>((resolve, reject) => {
                const request = store.put(brandItem);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
    }

    async storeImage(url: string, blob: Blob): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["images"], "readwrite");
        const store = transaction.objectStore("images");

        const imageData: StoredImage = {
            url,
            blob,
            timestamp: Date.now(),
        };

        await new Promise<void>((resolve, reject) => {
            const request = store.put(imageData);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async storeBrandingFile(url: string, blob: Blob): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["brandingFiles"], "readwrite");
        const store = transaction.objectStore("brandingFiles");

        const brandingFileData: StoredBrandingFile = {
            url,
            blob,
            timestamp: Date.now(),
        };

        await new Promise<void>((resolve, reject) => {
            const request = store.put(brandingFileData);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async getProducts(): Promise<ProductInterface[]> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["products"], "readonly");
        const store = transaction.objectStore("products");

        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getCategories(): Promise<CategoryInterface[]> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["categories"], "readonly");
        const store = transaction.objectStore("categories");

        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getFilters(): Promise<FilterListInterface[]> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["filters"], "readonly");
        const store = transaction.objectStore("filters");

        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getBranding(): Promise<BrandingInterface[]> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["branding"], "readonly");
        const store = transaction.objectStore("branding");

        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getImage(url: string): Promise<StoredImage | null> {
        if (!url) {
            return null;
        }

        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["images"], "readonly");
        const store = transaction.objectStore("images");

        return new Promise((resolve, reject) => {
            const request = store.get(url);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async getBrandingFile(url: string): Promise<StoredBrandingFile | null> {
        if (!url) {
            return null;
        }

        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["brandingFiles"], "readonly");
        const store = transaction.objectStore("brandingFiles");

        return new Promise((resolve, reject) => {
            const request = store.get(url);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async setMetadata(key: string, value: unknown): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["metadata"], "readwrite");
        const store = transaction.objectStore("metadata");

        await new Promise<void>((resolve, reject) => {
            const request = store.put({ key, value });
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async getMetadata(key: string): Promise<unknown> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["metadata"], "readonly");
        const store = transaction.objectStore("metadata");

        return new Promise((resolve, reject) => {
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result?.value || null);
            request.onerror = () => reject(request.error);
        });
    }

    async clearAll(): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const stores = [
            "products",
            "categories",
            "filters",
            "branding",
            "images",
            "metadata",
        ];
        const transaction = this.db.transaction(stores, "readwrite");

        for (const storeName of stores) {
            const store = transaction.objectStore(storeName);
            await new Promise<void>((resolve, reject) => {
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
    }

    // Helper methods for incremental sync
    async getProductById(
        id: number,
    ): Promise<ProductDetailsDataInterface | null> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["products"], "readonly");
        const store = transaction.objectStore("products");

        return new Promise((resolve, reject) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async getCategoryById(id: number): Promise<CategoryInterface | null> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["categories"], "readonly");
        const store = transaction.objectStore("categories");

        return new Promise((resolve, reject) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async getFilterById(id: number): Promise<FilterListInterface | null> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["filters"], "readonly");
        const store = transaction.objectStore("filters");

        return new Promise((resolve, reject) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async getBrandingById(id: number): Promise<BrandingInterface | null> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["branding"], "readonly");
        const store = transaction.objectStore("branding");

        return new Promise((resolve, reject) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async deleteProduct(id: number): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["products"], "readwrite");
        const store = transaction.objectStore("products");

        await new Promise<void>((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async deleteCategory(id: number): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["categories"], "readwrite");
        const store = transaction.objectStore("categories");

        await new Promise<void>((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async deleteFilter(id: number): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["filters"], "readwrite");
        const store = transaction.objectStore("filters");

        await new Promise<void>((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async deleteBranding(id: number): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["branding"], "readwrite");
        const store = transaction.objectStore("branding");

        await new Promise<void>((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async deleteImage(url: string): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["images"], "readwrite");
        const store = transaction.objectStore("images");

        await new Promise<void>((resolve, reject) => {
            const request = store.delete(url);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async deleteBrandingFile(url: string): Promise<void> {
        if (!this.db) throw new Error("Database not initialized");

        const transaction = this.db.transaction(["brandingFiles"], "readwrite");
        const store = transaction.objectStore("brandingFiles");

        await new Promise<void>((resolve, reject) => {
            const request = store.delete(url);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    private normalizeForComparison(obj: unknown): string {
        // Remove volatile fields and normalize for comparison
        const normalized = JSON.parse(
            JSON.stringify(obj, (_key, value) => {
                // Skip functions and undefined values
                if (typeof value === "function" || value === undefined) {
                    return undefined;
                }
                return value;
            }),
        );

        return JSON.stringify(normalized);
    }

    async hasProductChanged(newProduct: ProductInterface): Promise<boolean> {
        const existingProduct = await this.getProductById(newProduct.id);
        if (!existingProduct) return true; // New product

        const existingNormalized = this.normalizeForComparison(existingProduct);
        const newNormalized = this.normalizeForComparison(newProduct);

        return existingNormalized !== newNormalized;
    }

    async hasCategoryChanged(newCategory: CategoryInterface): Promise<boolean> {
        const existingCategory = await this.getCategoryById(newCategory.id);
        if (!existingCategory) return true; // New category

        const existingNormalized =
            this.normalizeForComparison(existingCategory);
        const newNormalized = this.normalizeForComparison(newCategory);

        return existingNormalized !== newNormalized;
    }

    async hasFilterChanged(newFilter: FilterListInterface): Promise<boolean> {
        const existingFilter = await this.getFilterById(newFilter.id);
        if (!existingFilter) return true; // New filter

        const existingNormalized = this.normalizeForComparison(existingFilter);
        const newNormalized = this.normalizeForComparison(newFilter);

        return existingNormalized !== newNormalized;
    }

    async hasBrandingChanged(newBranding: BrandingInterface): Promise<boolean> {
        const existingBranding = await this.getBrandingById(newBranding.id);
        if (!existingBranding) return true; // New branding

        const existingNormalized =
            this.normalizeForComparison(existingBranding);
        const newNormalized = this.normalizeForComparison(newBranding);

        return existingNormalized !== newNormalized;
    }
}

export const indexedDBManager = new IndexedDBManager();
